/** Frozen request/response shapes for the 65 non-smoke operations (T024). */
import { z } from 'zod';

import { ERROR_CODES } from './errors';
import { fieldIssueSchema } from './issues';
import { messageKeySchema } from './types';

const empty = z.object({}).strict();
const id = z.string().trim().min(1).max(1_500).refine((value) => !value.includes('/'), 'must be one id');
const shortText = z.string().trim().min(1).max(500);
const date = z.iso.date();
const instant = z.iso.datetime({ offset: true });
const cursor = z.string().min(1).max(4_096);
const batchKey = z.uuid();
const jsonObject = z.record(z.string(), z.json());
/**
 * *(T095)* The administrator's instruction for one selected passage.
 *
 * Bounded because it is stored, replayed on every retry and sent to a provider: an unbounded field
 * here is an unbounded document, an unbounded prompt and an unbounded cost. Trimmed and non-empty,
 * because an instruction of spaces is not an instruction.
 */
const selectionInstruction = z.string().trim().min(1).max(1_000);

/**
 * *(T096)* The administrator-only operational state of one selection edit, as a **filter** value.
 *
 * Three values, and they are not the job's stored statuses: `queued` and `running` are one thing to
 * an administrator — work is under way — so the vocabulary an interface filters and badges by is its
 * own. The four public Question statuses are untouched by it, and no student response carries it.
 */
export const SELECTION_SUBSTATUSES = Object.freeze(['running', 'ready', 'error'] as const);

/** The wire vocabulary, so the server, the worker and the browser all name these three once. */
export type SelectionSubstatus = (typeof SELECTION_SUBSTATUSES)[number];

const selectionSubstatus = z.enum(SELECTION_SUBSTATUSES);
const nullableString = z.string().nullable();

export const operationErrorPayloadSchema = z
  .object({
    code: z.enum(ERROR_CODES),
    messageKey: messageKeySchema,
    issues: z.array(fieldIssueSchema).optional(),
  })
  .strict();

/**
 * *(002-course-content-reader R1)* The rich-text document version this feature introduces.
 *
 * A **shape** constant, not a content one: the deep allowlist lives in `packages/domain`, and this
 * wire schema deliberately keeps `content` as opaque JSON so the contract cannot drift from the
 * validator. What the version buys is a stored document that says which rules produced it, which is
 * what lets a legacy v1 document be normalized in memory instead of being back-filled (R6).
 */
export const RICH_TEXT_VERSION = 2;

/**
 * `version` is **optional on the wire and never required**, and that is the whole backward-compatible
 * story: every document stored before this feature carries no version and still validates here, while
 * a v2 document carries exactly `2` and nothing else. A caller cannot invent a version 3 the server
 * has no rules for.
 */
export const richTextDocSchema = z
  .object({
    type: z.literal('doc'),
    version: z.literal(RICH_TEXT_VERSION).optional(),
    content: z.array(z.json()),
  })
  .strict();

export const richTextContentSchema = z
  .object({ doc: richTextDocSchema, text: z.string() })
  .strict();

/**
 * *(002-course-content-reader R2)* One endpoint of a structural passage: which text block, and how
 * far into it.
 *
 * This replaces the `blockPath` + character-offset pair the single-block selection used. A path is
 * an index into the tree, so inserting a paragraph above the target moves it; a `blockId` is an
 * identity Tiptap's UniqueID extension preserves through split, merge, undo/redo and paste. That is
 * what lets one anchor survive both an unrelated edit elsewhere in the document and a re-pagination
 * on a different device.
 *
 * Bounded and separator-free for the same reason every other id in this file is: it reaches a stored
 * document and is compared against derived content, never concatenated into a path.
 */
const blockId = z
  .string()
  .trim()
  .min(1)
  .max(200)
  .refine((value) => !value.includes('/'), 'must be one block id');

const blockPoint = z.object({ blockId, offset: z.number().int().nonnegative() }).strict();

/**
 * The two endpoints of one contiguous range, in document order.
 *
 * Ordering, contiguity, non-emptiness and non-overlap are **server** decisions — they need the stored
 * document, which no schema has — so this shape proves only that two well-formed endpoints arrived.
 * `contracts/operations.md` maps the rest to `INVALID_ARGUMENT` and `FAILED_PRECONDITION`.
 */
const structuralRange = z.object({ start: blockPoint, end: blockPoint });

/** *(FR-008)* The three raster formats the feature accepts. SVG and GIF are refused by omission. */
export const CONTENT_IMAGE_CONTENT_TYPES = Object.freeze([
  'image/jpeg',
  'image/png',
  'image/webp',
] as const);

export type ContentImageContentType = (typeof CONTENT_IMAGE_CONTENT_TYPES)[number];

const imageContentType = z.enum(CONTENT_IMAGE_CONTENT_TYPES);

/** *(FR-008)* Ten mebibytes, stated once so the wire, the Storage rules and the UI cannot disagree. */
export const MAX_CONTENT_IMAGE_BYTES = 10 * 1024 * 1024;

/** *(FR-011)* The four display widths an administrator may choose, as percentages. */
export const CONTENT_IMAGE_WIDTHS = Object.freeze([25, 50, 75, 100] as const);

export type ContentImageWidth = (typeof CONTENT_IMAGE_WIDTHS)[number];

/** *(data-model.md § ContentAsset)* What an asset was uploaded for; it can never be re-purposed. */
export const CONTENT_ASSET_PURPOSES = Object.freeze([
  'course_cover',
  'material_image',
  'explanation_image',
] as const);

export type ContentAssetPurpose = (typeof CONTENT_ASSET_PURPOSES)[number];

const contentAssetPurpose = z.enum(CONTENT_ASSET_PURPOSES);

/**
 * How many asset ids one access call may name, and how many private reader rows one response carries.
 *
 * `plan.md` states both as fifty. The bound is the point: a reading material with many images must
 * open with **one** authorization round trip rather than one per image, and an unbounded batch would
 * turn a single call into an unbounded fan-out of signature work.
 */
export const MAX_ASSET_ACCESS_IDS = 50;
export const MAX_READER_RECORDS = 50;
/**
 * *(Re-audit N02, OD-SR2)* How many overlapped highlights one `reader.highlights.replace` may remove.
 * Never more than one material can hold, so a replacement can always name every mark it overlaps.
 */
export const MAX_HIGHLIGHT_REPLACEMENTS = MAX_READER_RECORDS;

/** *(FR-026)* The five private marker colors. A sixth is a schema failure, not a rendering choice. */
export const HIGHLIGHT_COLORS = Object.freeze([
  'yellow',
  'green',
  'blue',
  'pink',
  // *(OD-2026-09-23)* Orange replaces purple.
  'orange',
] as const);

export type HighlightColor = (typeof HIGHLIGHT_COLORS)[number];

const highlightColor = z.enum(HIGHLIGHT_COLORS);
/** *(OD-2026-09-23)* A private note on a highlight; `null` clears it. Trimmed text, at most 2,000 characters. */
const highlightNote = z.string().trim().max(2_000).nullable();

const accountStatus = z.enum(['pending', 'approved', 'rejected', 'deactivated']);
const role = z.enum(['student', 'admin']);
/**
 * *(FR-COURSE-01/05, CE-1)* The course type. `continuing` is continuing education („განგრძობითი
 * სწავლება“): a course without groups whose one hidden technical group the server creates with it.
 */
export const COURSE_TYPES = Object.freeze(['self_paced', 'full', 'continuing'] as const);
export type CourseType = (typeof COURSE_TYPES)[number];
const courseType = z.enum(COURSE_TYPES);

/**
 * *(Owner decision 2026-09-25, OD-AI3)* The Gemini instruction a course's questions are answered with:
 * `standard` (A, full courses), `fundamentals` (B, self-paced courses), `pediatric` (C, chosen per
 * course). A course stores a choice only when the administrator made one; otherwise its type decides,
 * and continuing education always uses A.
 */
export const AI_PROFILES = Object.freeze(['standard', 'fundamentals', 'pediatric'] as const);
export type AiProfile = (typeof AI_PROFILES)[number];
const aiProfile = z.enum(AI_PROFILES);

/** The instruction a course actually uses: its own choice, or its type's default. */
export function effectiveAiProfile(type: CourseType, choice: AiProfile | null | undefined): AiProfile {
  if (type === 'continuing') return 'standard';
  if (choice !== null && choice !== undefined) return choice;
  return type === 'self_paced' ? 'fundamentals' : 'standard';
}
// *(OD-2026-09-23)* `draft`: a course being prepared — invisible to students and closed to enrolment.
const courseStatus = z.enum(['draft', 'active', 'archived']);
const questionStatus = z.enum(['new', 'preparing', 'prepared', 'confirmed']);
/**
 * The student Q&A scopes. *(FR-VIS-10, CE-5)* `all` is the platform base of a continuing-education
 * member within term: every confirmed question on the platform plus the member's own. The server
 * refuses it (`FORBIDDEN`) for every other caller, including an expired CE member.
 */
export const STUDENT_QA_SCOPES = Object.freeze(['mine', 'group', 'all'] as const);
export type StudentQaScope = (typeof STUDENT_QA_SCOPES)[number];
const visibilityScope = z.enum(STUDENT_QA_SCOPES);
const questionVisibilityScope = z.enum(['group', 'author']);
const sort = z.enum(['createdAt_desc', 'createdAt_asc']).default('createdAt_desc');

const enrollmentSummary = z
  .object({
    enrollmentId: id,
    courseId: id,
    groupId: id,
    openedAt: instant,
    entStart: instant,
    entEnd: instant,
    remainingDays: z.number().int(),
    canAsk: z.boolean(),
    status: z.enum(['active', 'closed']),
    /*
     * *(CE-10 web mapping)* The enrolled course's type, so a client can tell a continuing-education
     * enrolment apart without a second read. Optional on the wire only so an older fixture still
     * validates; the server always projects it.
     */
    courseType: courseType.optional(),
  })
  .strict();

export const meResponseSchema = z
  .object({
    account: z
      .object({
        uid: id,
        email: z.email(),
        firstName: z.string(),
        lastName: z.string(),
        phone: nullableString,
        role,
        status: accountStatus,
      })
      .strict(),
    enrollments: z.array(enrollmentSummary),
    /*
     * *(FR-VIS-10, CE-5/CE-6)* Whether the caller is a continuing-education member **within term**
     * right now: they may read with scope `all`, and author names appear on CE questions. Derived by
     * the server from current state; optional on the wire only for older fixtures.
     */
    continuingAccess: z.boolean().optional(),
  })
  .strict();

const material = z
  .object({
    materialId: id,
    type: z.enum(['video_embed', 'rich_text', 'external_link', 'file']),
    title: z.string(),
    embedUrl: z.url().optional(),
    richText: richTextContentSchema.optional(),
    /*
     * *(2026-09-26)* A reading body too large for one stored row is kept in Storage and is not carried
     * by a course read: the listing gets its size and opening words, and `materials.body` returns the
     * document when the reader opens it. Exactly one of `richText` and `body` is present.
     */
    body: z
      .object({
        bytes: z.number().int().nonnegative(),
        textLength: z.number().int().nonnegative(),
        blockCount: z.number().int().nonnegative(),
        excerpt: z.string(),
      })
      .strict()
      .optional(),
    externalUrl: z.url().optional(),
    fileName: z.string().optional(),
    /*
     * *(002-course-content-reader)* The immutable version the projected document belongs to.
     *
     * Optional because only rich reading materials have one, and because a material stored before
     * this feature has none until its next administrator save (R6: no back-fill). Every anchor a
     * student or an administrator resolves names this value, so a reader that opened version 4 and a
     * highlight created against version 3 are distinguishable rather than silently mixed.
     */
    contentVersionId: id.optional(),
    /*
     * The opaque protected-image ids the active document references, derived by the server from the
     * document itself. Never a storage path and never a URL (FR-012): the browser exchanges these for
     * short-lived URLs through one batched `contentAssets.fileAccess`.
     */
    assetIds: z.array(id).max(MAX_ASSET_ACCESS_IDS).optional(),
    /*
     * The administrator explanations already resolved against `contentVersionId`.
     *
     * They ride with the material because they share its entitlement and opening rules exactly
     * (FR-021) — a second authorized call would re-derive the same snapshot — and because an
     * explanation that resolved against a *different* version of the document than the one being
     * rendered would mark the wrong words. Private reader state is deliberately **not** here: it is
     * per-account, and this projection is shared.
     */
    explanations: z.array(jsonObject).max(200).optional(),
    /*
     * *(PRD-07 § 3.4)* When this student marked this material finished, or `null` when they have not.
     *
     * Additive and **derived per caller**, unlike everything above it: the value comes from the
     * calling account's own private completion row, resolved inside the same authorized snapshot that
     * decided this material is visible at all. `null` is the ordinary "not completed" state, including
     * the state of a material whose mark was explicitly removed — `materials.completion.set` keeps the
     * row with `completed:false` rather than deleting it (FREEZE-3).
     *
     * Optional in the schema for the reason every additive field here is: `courses.listMine` and
     * `courses.get` are the only producers and always project it, but a required key would invalidate
     * every fixture written against the shape before this feature. A hidden material never reaches
     * this projection at all, so there is no marked-but-invisible case to describe.
     */
    completedAt: instant.nullable().optional(),
    /*
     * *(OD-2026-09-23)* A published material the schedule has not opened yet for this student: shown
     * with `opensAt` and without any content field, counted in the progress total, never completed or openable.
     */
    locked: z.literal(true).optional(),
    opensAt: instant.optional(),
    order: z.number().int(),
    published: z.boolean(),
  })
  .strict();

const section = z
  .object({
    sectionId: id,
    title: z.string(),
    scope: z.enum(['course', 'group']),
    groupId: id.optional(),
    order: z.number().int(),
    materials: z.array(material),
  })
  .strict();

const course = z
  .object({
    courseId: id,
    name: z.string(),
    type: courseType,
    /** The server-derived plain projection — still here, still what search and fallbacks read (R6). */
    description: z.string(),
    /*
     * *(002-course-content-reader, FR-005/FR-006)* The structured description, when one has been
     * authored. Absent for every course stored before this feature, which is exactly why `description`
     * above is not replaced: a legacy course stays readable with no migration and no retyping.
     */
    descriptionRich: richTextContentSchema.optional(),
    /*
     * *(FR-012, FR-014)* The opaque cover asset id — **never** `coverImagePath`.
     *
     * The legacy field is a Storage path, and `BOUNDARIES.md` § 5.4 is that an object path never
     * leaves the server; projecting one also told every browser the internal layout of the bucket.
     * It remains in Firestore for compatibility and lifecycle cleanup only (`data-model.md` § Course)
     * and is no longer editable or projected. `null` is the ordinary "no cover yet" state every
     * surface renders its fallback for.
     */
    coverAssetId: z.string().nullable(),
    /** The enrolled group facts the student needs on the course card. */
    group: z
      .object({
        groupId: id,
        name: z.string(),
        startAt: instant.nullable(),
        endAt: instant.nullable(),
        meetingDay: z.number().int().min(0).max(6).nullable(),
        meetingTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).nullable(),
      })
      .strict()
      .optional(),
    status: courseStatus,
    /** *(OD-2026-09-23)* Present only on an enrolled course that has not opened yet: when it opens. */
    opensAt: instant.optional(),
    /*
     * *(FR-ENT-09, CE-7)* Present only on a continuing-education course whose access has ended: the
     * course stays on `courses.listMine` with no sections and no materials, and `courses.get`, the
     * reader, file access, material assets and completion refuse it until a repurchase.
     */
    expired: z.literal(true).optional(),
    /*
     * *(PRD-07 § 3.4, FREEZE-5)* This student's progress through **this** projection.
     *
     * `visibleCount` is the number of materials the response itself carries — the authorized,
     * published, released, group-scoped set for this caller at read time — and `completedCount` counts
     * the completion rows whose material is in that same set. A completion on a material that has
     * since become invisible is retained in storage and **not** counted, so `completedCount` can never
     * exceed `visibleCount` and the denominator can never disclose a hidden material's existence.
     *
     * Server-derived, and there is deliberately no separate progress read: a client computing its own
     * denominator would be a client that could count something this projection omitted.
     *
     * **The freeze is expressed in the schema, not only in the producer.** FREEZE-5 states that
     * `completedCount ≤ visibleCount` can never be violated and names `9/8` as the impossible value
     * it forbids. `progressOf` makes that true by construction — numerator and denominator come from
     * one array — but a contract that *admitted* `9/8` would let any future producer, or a compromised
     * one, put an unrenderable fraction on a course card and have every layer accept it. The refusal
     * belongs here, where `dispatch` checks it on the way out and `api.ts` checks it on the way in.
     */
    progress: z
      .object({
        // Spelled out rather than reusing `wholeCount`: that helper is declared below this object and
        // would be in its temporal dead zone when this schema is built at module load.
        completedCount: z.number().int().nonnegative(),
        visibleCount: z.number().int().nonnegative(),
      })
      .strict()
      .refine(
        (value) => value.completedCount <= value.visibleCount,
        // Data-free: it names the invariant, never the course, the student or either count.
        { message: 'completedCount may not exceed visibleCount', path: ['completedCount'] },
      )
      .optional(),
    sections: z.array(section),
  })
  .strict();

export const questionListItemSchema = z
  .object({
    questionId: id,
    courseId: id,
    groupId: id,
    text: z.string(),
    edited: z.boolean(),
    displayStatus: z.enum(['waiting', 'answered']),
    createdAt: instant,
    answeredAt: instant.optional(),
    updatedAt: instant.optional(),
    authorDisplayName: z.string().optional(),
    isMine: z.boolean(),
    isFavorite: z.boolean(),
    answer: richTextContentSchema.optional(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.displayStatus === 'answered' && value.answer === undefined) {
      context.addIssue({ code: 'custom', path: ['answer'], message: 'answered items require answer' });
    }
    if (value.displayStatus === 'waiting' && value.answer !== undefined) {
      context.addIssue({ code: 'custom', path: ['answer'], message: 'waiting items never expose answer' });
    }
  });

const questionListRequest = z
  .object({
    courseId: id.optional(),
    scope: visibilityScope,
    dateFrom: date.optional(),
    dateTo: date.optional(),
    sort,
    cursor: cursor.optional(),
    includeMe: z.boolean().optional(),
  })
  .strict();

const questionPage = z
  .object({
    items: z.array(questionListItemSchema).max(20),
    nextCursor: cursor.optional(),
    total: z.number().int().nonnegative(),
    me: meResponseSchema.optional(),
  })
  .strict();

const searchResponse = z
  .object({
    suggestions: z.array(z.string()).max(8),
    items: z.array(questionListItemSchema).max(20),
    nextCursor: cursor.optional(),
    total: z.number().int().nonnegative(),
  })
  .strict();

/** *(OD-2026-09-24, OD-S3)* The Admin Students register chips, in the design's order. */
export const STUDENT_DIRECTORY_STATUSES = Object.freeze(['all', 'active', 'invited', 'suspended', 'ending'] as const);
export type StudentDirectoryStatus = (typeof STUDENT_DIRECTORY_STATUSES)[number];
/** *(OD-2026-09-24, OD-S4)* The two register orders: newest first (default) and by name. */
const STUDENT_DIRECTORY_SORTS = Object.freeze(['newest', 'name'] as const);
/** *(OD-2026-09-24, OD-S3)* How close an access end must be to put a student under „წვდომა იწურება“. */
export const STUDENT_ACCESS_ENDING_DAYS = 14;
/** *(OD-2026-09-24, OD-G6)* How many deleted materials one Trash read returns. */
export const MAX_DELETED_MATERIALS_LISTED = 200;
/** *(OD-2026-10-12)* How many deleted sections one section Trash read returns. */
export const MAX_DELETED_SECTIONS_LISTED = 100;

const adminPageRequest = z.object({ pageSize: z.number().int().min(1).max(50).default(50), cursor: cursor.optional() });
const pageOf = (item: z.ZodType) =>
  z.object({ items: z.array(item).max(50), nextCursor: cursor.optional(), total: z.number().int().nonnegative() }).strict();
const itemResponse = z.object({ item: jsonObject }).strict();
const okResponse = z.object({ ok: z.literal(true) }).strict();
/** *(ledger 59)* A counted quantity: a whole number of things, never negative. */
const wholeCount = z.number().int().nonnegative();

const genericApplied = z.object({ id, before: jsonObject, after: jsonObject }).strict();
const genericSkipped = z.object({ id, reason: z.string().min(1) }).strict();
const genericFailed = z.object({ id, error: operationErrorPayloadSchema }).strict();
export const batchResponseSchema = z
  .object({
    batchId: id.nullable(),
    applied: z.array(genericApplied),
    skipped: z.array(genericSkipped),
    failed: z.array(genericFailed),
  })
  .strict();

const batchRequest = (params: z.ZodType = jsonObject) =>
  z.object({ ids: z.array(id).min(1).max(500), params, batchKey: batchKey.optional() }).strict();

const optionalName = z.string().trim().max(100).nullable().optional();
const profilePatch = z
  .object({ firstName: optionalName, lastName: optionalName, phone: z.string().trim().max(50).nullable().optional() })
  .strict();
const listFilters = adminPageRequest.extend({ q: z.string().trim().max(500).optional() }).strict();

/**
 * *(AR-09)* The reusable course template — `docs/design/admin-redesign-addendum.md` § 3.
 *
 * Two owner-stated numbers and nothing else. `meetingsPerWeek` defaults to 1 and stays editable;
 * `lectureDurationMinutes` supports 60 through 240. The template is a **default for a new group**, not
 * a schedule: it creates, moves and deletes no lecture, and changing it writes to no existing group.
 *
 * It is optional on the wire so that a caller written before this addendum still validates, and so an
 * update can leave it alone. Absent means "unchanged" on an update and "the documented default" on a
 * create; a course stored before AR-09 reads back as that same default, which is why no migration and
 * no backfill write exists.
 */
const courseTemplate = z
  .object({
    meetingsPerWeek: z.number().int().min(1).max(7),
    lectureDurationMinutes: z.number().int().min(60).max(240),
    /*
     * *(ledger 45, 58)* The course's own length, in **weeks**.
     *
     * The owner's annotation 45 states it: "Full-course duration is chosen in weeks; start date plus
     * duration derives end date". The course owns the default and a group derives its interval from
     * it, so the number lives with the rest of the reusable template rather than being retyped into
     * every group. Optional for the same reason the other two are: a course stored before this reads
     * back as the documented default, so nothing is migrated.
     */
    weeks: z.number().int().min(1).max(104).optional(),
    /*
     * *(ledger 35, 46, 58)* The ordered meeting/topic template.
     *
     * Annotation 46: "Ordered lecture topics belong to the course template; a new group's dated
     * schedule is generated from start date and weekly cadence rather than manually entered row by
     * row." These are the topic names in teaching order. They are names, never dates: a date belongs
     * to a group's own generated schedule, and the template must stay reusable across every group.
     *
     * The cap matches `MAX_LECTURE_NUMBER` on the client, so a template can never describe a schedule
     * a group is not allowed to hold.
     */
    topics: z.array(shortText).max(200).optional(),
  })
  .strict();

/**
 * *(ledger 44)* The lecture durations the interface offers.
 *
 * The owner's decision names these six: "Lecture duration is selected from 60, 90, 120, 150, 180 or
 * 240 minutes." The **wire** keeps the documented 60–240 range, because a course stored before this
 * annotation may legitimately carry any value in it and a narrower schema would make that course
 * unreadable rather than merely unfashionable. The choices are exported so the administrator surface
 * offers exactly these and nothing else, which is where the owner's decision actually applies.
 */
export const LECTURE_DURATION_CHOICES: readonly number[] = Object.freeze([60, 90, 120, 150, 180, 240]);

/**
 * *(AR-09 correction)* The immutable token that binds one preview to the apply that follows it.
 *
 * `admin.courses.applyTemplatePreview` answers with the token of the template it previewed, and
 * `admin.courses.applyTemplate` must send that same token back. The server re-derives it from the
 * course inside every transaction that would write, so a template edited between the two is refused
 * rather than applied: what the administrator confirmed and what lands are the same template or
 * nothing at all.
 *
 * It also rides inside `params`, which is what the frozen batch envelope hashes into a batch's
 * request fingerprint. A retry that reuses a `batchKey` with a different token is therefore a
 * different request and answers `CONFLICT` — one batch can never mix two template versions.
 */
const templateVersion = z.string().trim().min(1).max(200);

/**
 * *(AR-09 correction)* The propagation pair's own batch envelope, capped at two hundred ids.
 *
 * `docs/design/admin-redesign-addendum.md` § 3.3 states the cap; the generic `batchRequest` allows
 * five hundred. A cap a document states and a schema does not enforce is not a cap, so this one is
 * enforced here — at the boundary every caller crosses — rather than inside a handler.
 */
const templatePropagationRequest = (params: z.ZodType) =>
  z.object({ ids: z.array(id).min(1).max(200), params, batchKey: batchKey.optional() }).strict();

/** The preview answers the frozen batch shape *plus* the token an apply has to quote back. */
export const templatePreviewResponseSchema = batchResponseSchema.extend({ templateVersion }).strict();

/**
 * *(002-course-content-reader)* What `admin.courses.create` / `.update` accept.
 *
 * Two changes, both from the owner-approved specification:
 *
 *  - **`descriptionDoc` is added.** When it is present the server validates it against rich-text v2
 *    and derives `description` from it, so the plain projection can never disagree with the document
 *    it came from. `description` stays accepted on its own so a caller with no rich editor — and
 *    every course stored before this feature — still writes and reads exactly as before (FR-006).
 *  - **`coverImagePath` is removed and `coverAssetId` takes its place.** The old field was an
 *    administrator-typed *internal Storage path*; the interface even had to explain that the file was
 *    not uploaded from it and would not open as an image. FR-008/FR-009 replace that with a real
 *    protected upload, so what a save carries is the opaque id of an already-verified asset. `null`
 *    removes the cover; absent leaves it unchanged on an update.
 */
const courseInput = z
  .object({
    name: shortText,
    type: courseType,
    description: z.string().max(10_000),
    descriptionDoc: richTextDocSchema.optional(),
    coverAssetId: id.nullable().optional(),
    template: courseTemplate.optional(),
    /** `null` returns the course to its type's default instruction; absent leaves it unchanged. */
    aiProfile: aiProfile.nullable().optional(),
  })
  .strict();

/**
 * *(T091)* The four orders the administrator curriculum lists offer (FR-PANEL-02).
 *
 * They are deliberately not the student `sort` above: a curriculum panel is browsed by name, and
 * `createdAt` is the secondary "what did we add recently" view. Every order is tie-broken by document
 * id on the server, so a page boundary is stable even when two rows share a name or an instant.
 */
const curriculumSort = z
  .enum(['name_asc', 'name_desc', 'createdAt_desc', 'createdAt_asc'])
  .default('name_asc');

/**
 * *(T091)* The shared filter/sort/page request of `admin.courses.list` and `admin.groups.list`.
 *
 * `listFilters` already carries `q`, `pageSize` and `cursor` for the administrator directories; the
 * two curriculum equality filters and the order are what this surface adds. `admin.groups.list`
 * extends it once more with `courseId`, because a group is browsed inside the course that owns it.
 */
/**
 * *(OD-7)* The three explicit lifecycle directories of `admin-redesign-addendum.md` § 8.6.
 *
 * Trash is **orthogonal** to the retained `status`, so this is a third axis rather than a fourth
 * status value: a trashed course keeps `active` or `archived` as the exact state a restore returns
 * it to (§ 8.3). The three views partition every row an administrator can reach:
 *
 *  - `active`   — the **default**: live rows whose status is `active`, and nothing else. § 8.3 and
 *                 AR-LC-07 make Archive a separate explicit view, so a default directory that also
 *                 returned archived rows would be the superseded behaviour, not the accepted one;
 *  - `archived` — live rows whose status is `archived`;
 *  - `trash`    — only the soft-deleted rows, with their retained status and deletion metadata.
 *
 * The field is **optional rather than defaulted on the wire** so the server can tell "no view asked
 * for" from "the active view asked for", which is what lets an existing caller that narrows with
 * `status: 'archived'` and no view keep reaching exactly the rows it always did. When a view *is*
 * named, it is the authority on the status axis and a contradicting `status` is refused rather than
 * silently answered with an empty page — the two cannot both narrow one equality.
 */
export const LIFECYCLE_VIEWS = Object.freeze(['active', 'archived', 'trash', 'draft'] as const);

export type LifecycleView = (typeof LIFECYCLE_VIEWS)[number];

/** The view a request without one means. Stated once so no surface invents a different default. */
export const DEFAULT_LIFECYCLE_VIEW: LifecycleView = 'active';

const lifecycleView = z.enum(LIFECYCLE_VIEWS).optional();

const curriculumListFilters = listFilters
  .extend({
    status: courseStatus.optional(),
    type: courseType.optional(),
    sort: curriculumSort,
    view: lifecycleView,
  })
  .strict();

/**
 * *(OD-7)* What a deletion would remove, as counts of things — never as the things themselves.
 *
 * `admin-redesign-addendum.md` § 8.3 requires a **read-only impact preview** before a destructive
 * confirmation, and § 8.7 AR-LC-07 requires the interface to show "impact counts". Counts are all
 * this shape carries: no id, no title, no file name and no student name, so a preview discloses
 * nothing the administrator could not already list and nothing is left behind in a log or a dialog
 * transcript that names a person.
 *
 * `exact` is the honesty flag. The preview counts over one bounded scan, exactly as every other
 * administrator read does; a set larger than that bound answers with what it counted and says the
 * number is a floor rather than quietly presenting a truncated sum as a total.
 */
export const lifecycleImpactCountsSchema = z
  .object({
    groups: wholeCount,
    sections: wholeCount,
    materials: wholeCount,
    files: wholeCount,
    enrollments: wholeCount,
    preInvites: wholeCount,
    questions: wholeCount,
    exact: z.boolean(),
  })
  .strict();

/**
 * *(OD-7)* Why a deletion is refused, as a stable code the message catalogue turns into Georgian.
 *
 * One code today: § 8.4's single named precondition failure. It is an enum rather than a boolean so
 * a future second reason does not have to re-shape the response, and `null` is written explicitly so
 * "nothing blocks this" is a stated fact rather than an absent field.
 */
export const LIFECYCLE_BLOCK_REASONS = Object.freeze(['GROUP_HAS_STUDENTS'] as const);

export type LifecycleBlockReason = (typeof LIFECYCLE_BLOCK_REASONS)[number];

/**
 * *(OD-7)* The read-only answer to "what would deleting this remove?".
 *
 * `name` is the human-readable name the dialog shows, because `DESIGN.md` forbids a raw id as a
 * primary label. `status` is the retained state a restore would return the item to.
 *
 * One `counts` shape serves both aggregates, and every figure in it is genuinely removed by the
 * deletion it describes. A standalone group's aggregate includes the Questions whose current
 * `groupId` is that group — with their text and answer versions, their related AI-call rows and the
 * Favorites that reference them — exactly as the parent course's aggregate includes the course's. No
 * Account and no other course's data is ever in either count.
 */
export const lifecycleImpactSchema = z
  .object({
    targetType: z.enum(['course', 'group']),
    targetId: id,
    name: z.string(),
    status: courseStatus,
    deletable: z.boolean(),
    blockedBy: z.enum(LIFECYCLE_BLOCK_REASONS).nullable(),
    counts: lifecycleImpactCountsSchema,
  })
  .strict();

/**
 * *(OD-7)* What a completed trash, restore or unarchive answers with.
 *
 * `deletedAt` and `purgeDueAt` are present together or absent together: the due instant is derived
 * from the deletion instant by the server's own retention constant, so a client never computes the
 * thirty days itself and two surfaces can never disagree about when something disappears.
 */
export const lifecycleStateSchema = z
  .object({
    targetType: z.enum(['course', 'group']),
    targetId: id,
    status: courseStatus,
    deletedAt: instant.nullable(),
    purgeDueAt: instant.nullable(),
  })
  .strict();

const lifecycleCourseRequest = z.object({ courseId: id }).strict();
const lifecycleGroupRequest = z.object({ groupId: id }).strict();

const lecture = z
  .object({ number: z.number().int().min(1), date, time: z.string().regex(/^\d{2}:\d{2}$/).optional() })
  .strict();
const groupFields = z
  .object({
    courseId: id,
    name: shortText,
    type: courseType,
    startAt: instant.nullable(),
    endAt: instant.nullable(),
    extraWeeks: z.number().int().nonnegative(),
    lectureSchedule: z.array(lecture),
    months: z.number().int().min(1).nullable(),
    /*
     * *(AR-09)* The group's own copy of the course template, taken at creation.
     *
     * A copy rather than a reference, because that is what the owner decision requires: "New groups
     * inherit the current course template. Later template changes never automatically alter active or
     * completed groups." A reference would make every course edit silently retroactive.
     *
     * Absent on create means "inherit the course's current template", which is the ordinary path; a
     * value is a confirmation the server re-derives rather than trusts.
     */
    template: courseTemplate.optional(),
  })
  .strict();

const sectionFields = z
  .object({
    courseId: id,
    title: shortText,
    scope: z.enum(['course', 'group']),
    groupId: id.nullable(),
    order: z.number().int().nonnegative(),
    published: z.boolean(),
  })
  .strict();

const materialType = z.enum(['video_embed', 'rich_text', 'external_link', 'file']);
const materialFields = z
  .object({
    courseId: id,
    sectionId: id,
    title: shortText,
    type: materialType,
    embedUrl: z.url().optional(),
    doc: richTextDocSchema.optional(),
    externalUrl: z.url().optional(),
    order: z.number().int().nonnegative(),
    published: z.boolean(),
    lectureNumber: z.number().int().min(1).nullable().optional(),
    lectureDate: date.nullable().optional(),
    // *(OD-2026-10-12, FR-MAT-07)* The groups of this course a material in a course section is
    // addressed to; null is the whole course. Empty and repeated lists are refused by the server with
    // their own codes, so the schema bounds only the size.
    groupIds: z.array(id).max(50).nullable().optional(),
    // *(OD-2026-10-03)* The legacy single audience older clients send; never together with `groupIds`.
    groupId: id.nullable().optional(),
  })
  .strict();

/**
 * *(CE-1, CE-10)* An enrolment or pre-invitation target: a group, **or** a continuing-education
 * course.
 *
 * A CE course has exactly one hidden technical group, so naming the course is enough and the client
 * never has to learn that group's id. Exactly one of the two is accepted — naming both would let the
 * pair disagree, and naming neither names nothing. The server still proves the course is CE; a
 * `courseId` of any other course names no technical group and is skipped as `GROUP_NOT_FOUND`.
 */
function exactlyOneTarget<T extends z.ZodType<{ readonly groupId?: string | undefined; readonly courseId?: string | undefined }>>(schema: T): T {
  return schema.refine(
    (value) => (value.groupId === undefined) !== (value.courseId === undefined),
    { message: 'name exactly one of groupId or courseId', path: ['groupId'] },
  ) as unknown as T;
}

const extension = z.discriminatedUnion('unit', [
  z.object({ unit: z.literal('months'), amount: z.number().int().min(1) }).strict(),
  z.object({ unit: z.literal('weeks'), amount: z.number().int().min(1) }).strict(),
  // *(CE-3)* Whole days from max(entEnd, now); a continuing-education repurchase is 365.
  z.object({ unit: z.literal('days'), amount: z.number().int().min(1) }).strict(),
  z.object({ unit: z.literal('exact'), exactDate: date }).strict(),
]);
const entitlementRequest = z
  .object({
    enrollmentIds: z.array(id).min(1).max(200),
    extension,
    reason: z.string().max(500).optional(),
    batchKey: batchKey.optional(),
  })
  .strict();
const entitlementApplied = z
  .object({ enrollmentId: id, accountId: id, courseId: id, previousEnd: instant, newEnd: instant })
  .strict();
const entitlementResponse = z
  .object({
    batchId: id.nullable(),
    applied: z.array(entitlementApplied),
    skipped: z.array(z.object({ enrollmentId: id, reason: z.enum([
      'NOT_FOUND', 'ENROLLMENT_CLOSED', 'COURSE_ARCHIVED', 'ACCOUNT_NOT_APPROVED', 'RESULT_IN_PAST', 'NO_CHANGE',
    ]) }).strict()),
    failed: z.array(z.object({ enrollmentId: id, error: operationErrorPayloadSchema }).strict()),
  })
  .strict();

/**
 * *(ledger 21–23)* How many values one multi-select filter may carry.
 *
 * The owner's annotations make the Q&A course, group and student filters multiple selections. A
 * multi-value equality reaches Firestore as an `in`, which is a disjunction: the deleted mode is
 * itself a second one, so ten values is the bound that keeps the pair inside the engine's disjunction
 * budget with room to spare. It is enforced here, at the boundary every caller crosses, rather than
 * inside a handler.
 */
export const MAX_FILTER_VALUES = 10;

const adminQuestionFilters = adminPageRequest
  .extend({
    courseId: id.optional(),
    groupId: id.optional(),
    accountId: id.optional(),
    /*
     * *(ledger 21–23)* The multiple-selection form of the three scope filters.
     *
     * They are **added** rather than replacing the singular fields: every accepted caller, test and
     * stored link that sends one id keeps working unchanged, and `BOUNDARIES.md` § 3 permits adding a
     * request field that grants no capability the administrator lacked. The server narrows at the
     * single deepest level the caller selected, so exactly one membership constraint is ever built.
     */
    courseIds: z.array(id).min(1).max(MAX_FILTER_VALUES).optional(),
    groupIds: z.array(id).min(1).max(MAX_FILTER_VALUES).optional(),
    accountIds: z.array(id).min(1).max(MAX_FILTER_VALUES).optional(),
    dateFrom: date.optional(),
    dateTo: date.optional(),
    status: questionStatus.optional(),
    /**
     * *(T096)* "Show me the questions that have a selection edit in this state."
     *
     * One value at a time, so the server builds one **equality** on a denormalized flag rather than a
     * disjunction — which is what keeps the page exact, the total exact and the cursor stable, and
     * what lets the existing course/group/student/date filters compose with it under one index.
     */
    selectionSubstatus: selectionSubstatus.optional(),
    needsAttention: z.boolean().optional(),
    deleted: z.boolean().optional(),
    q: z.string().trim().max(500).optional(),
    sort,
  })
  .strict();

const auditFilters = adminPageRequest
  .extend({
    action: z.string().optional(),
    actorId: id.optional(),
    targetId: id.optional(),
    batchId: id.optional(),
    dateFrom: date.optional(),
    dateTo: date.optional(),
  })
  .strict();

/* ------------------------------------------------------------------ *
 * *(002-course-content-reader)* Protected content assets, explanations and the private reader
 * ------------------------------------------------------------------ */

/**
 * The parent an asset access call is made **in the context of**.
 *
 * `courseId` is always present because entitlement is a course-scoped question; `sectionId` and
 * `materialId` are present exactly when the assets belong to a material rather than to the course
 * cover. The server re-derives the real owner of every id from its own record and refuses any
 * disagreement — these coordinates decide *which* authorization snapshot is taken, never who owns
 * what (`data-model.md` § State and authorization rules, rule 4).
 */
const assetParent = z
  .object({ courseId: id, sectionId: id.optional(), materialId: id.optional() })
  .strict();

const assetAccessRequest = assetParent
  .extend({ assetIds: z.array(id).min(1).max(MAX_ASSET_ACCESS_IDS) })
  .strict();

/**
 * One short-lived URL per asset, and nothing that outlives it.
 *
 * No storage path, no generation, no bucket and no content type: everything the browser needs to
 * render the image is the URL, and everything else would be a durable coordinate FR-012 forbids.
 */
const assetAccessResponse = z
  .object({
    assets: z
      .array(z.object({ assetId: id, url: z.url(), expiresAt: instant }).strict())
      .max(MAX_ASSET_ACCESS_IDS),
  })
  .strict();

/** The complete nested path of one material — the shape every explanation and reader call carries. */
const materialPath = z.object({ courseId: id, sectionId: id, materialId: id }).strict();

/**
 * The rich content of one explanation.
 *
 * `doc` only: the plain projection and the referenced asset ids are **derived** by the server, exactly
 * as they are for an answer and for a material. A caller that could send its own `text` could make
 * the searchable projection disagree with the document a student reads.
 */
const explanationContent = z.object({ doc: richTextDocSchema }).strict();

/**
 * *(FR-030, `contracts/operations.md` § Stable failures)* The revision a private or administrator
 * record is expected to still be at.
 *
 * Optimistic rather than last-write-wins: two devices editing the same highlight is an ordinary event
 * (FR-031), and the loser must be told to refetch — `CONFLICT` — instead of silently discarding the
 * other device's change.
 */
const expectedRevision = z.number().int().nonnegative();

/**
 * A client-generated idempotency id for one private reader record.
 *
 * The client mints it so that a retried create after a lost response is the *same* record rather than
 * a duplicate highlight (FR-031). It is a caller-supplied document id, so it is bounded and
 * separator-free like every other id here; the server still scopes it under the calling account, so
 * one student can never name another student's row.
 */
const readerRecordId = z.string().trim().min(1).max(200).refine(
  (value) => !value.includes('/'),
  'must be one id',
);

/**
 * The session one progress report belongs to, and where it sits in that session's own order.
 *
 * `reader.open` mints `sessionId` and a monotonically increasing `epoch`; `sequence` orders the
 * reports of one session. Both are required on every save, because "ignore an older device" is only
 * decidable if a report says which device-session and which point in it it came from. A stale report
 * is **not** an error: `contracts/operations.md` states it as an expected convergence no-op that
 * answers `{applied:false}` (R4).
 */
const readerSession = z
  .object({
    sessionId: id,
    epoch: z.number().int().nonnegative(),
    sequence: z.number().int().nonnegative(),
  })
  .strict();

/** Where the reader is: one point in one version of the content. Never a page number (FR-029). */
const readerPosition = z.object({ contentVersionId: id, point: blockPoint }).strict();

/**
 * *(Owner, 2026-10-12)* What a browser may report about itself — `client.report`.
 *
 * Students meet failures that happen only inside their own phone or browser: a PDF that will not open
 * on an older iPhone, a text reader that counts one page on one device. The server logs show only the
 * operations that reached it, so the client reports its own errors and a small reader diagnostic, and
 * the server writes them to Cloud Logging beside the operation records — and nowhere else.
 *
 * Every field is bounded and every object is strict, because this is free text from a browser that
 * ends up in a log line: an unbounded field is an unbounded log entry. The bounds are exported so the
 * reporter truncates to exactly what the server accepts, rather than to a guess that could make a
 * whole batch fail validation.
 */
export const CLIENT_REPORT_KINDS = Object.freeze([
  'error',
  'unhandledrejection',
  'pdf',
  'reader',
  'video',
  'other',
] as const);

export type ClientReportKind = (typeof CLIENT_REPORT_KINDS)[number];

export const CLIENT_REPORT_LIMITS = Object.freeze({
  userAgent: 400,
  platform: 60,
  language: 20,
  appVersion: 40,
  message: 500,
  where: 200,
  detailKey: 40,
  detailText: 200,
  detailKeys: 20,
  eventsPerReport: 10,
  /** A generous ceiling on a CSS pixel dimension, so a broken value cannot be an arbitrary number. */
  viewport: 100_000,
  devicePixelRatio: 20,
});

/**
 * A details key is a short identifier — `pages`, `fontsStatus` — never text. Keeping keys
 * name-shaped is what keeps the details map a set of measurements rather than a second message.
 */
export const CLIENT_REPORT_DETAIL_KEY_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,39}$/;

const clientReportDetails = z
  .record(
    z.string().regex(CLIENT_REPORT_DETAIL_KEY_PATTERN),
    z.union([z.string().max(CLIENT_REPORT_LIMITS.detailText), z.number(), z.boolean(), z.null()]),
  )
  .refine(
    (value) => Object.keys(value).length <= CLIENT_REPORT_LIMITS.detailKeys,
    `at most ${CLIENT_REPORT_LIMITS.detailKeys} details`,
  );

const clientReportClient = z
  .object({
    userAgent: z.string().max(CLIENT_REPORT_LIMITS.userAgent),
    platform: z.string().max(CLIENT_REPORT_LIMITS.platform),
    language: z.string().max(CLIENT_REPORT_LIMITS.language),
    viewport: z
      .object({
        w: z.number().int().nonnegative().max(CLIENT_REPORT_LIMITS.viewport),
        h: z.number().int().nonnegative().max(CLIENT_REPORT_LIMITS.viewport),
      })
      .strict(),
    dpr: z.number().positive().max(CLIENT_REPORT_LIMITS.devicePixelRatio),
    standalone: z.boolean(),
    appVersion: z.string().max(CLIENT_REPORT_LIMITS.appVersion),
  })
  .strict();

const clientReportEvent = z
  .object({
    kind: z.enum(CLIENT_REPORT_KINDS),
    message: z.string().max(CLIENT_REPORT_LIMITS.message),
    /** The route path only — never a query string or a fragment, which can carry tokens. */
    where: z.string().max(CLIENT_REPORT_LIMITS.where),
    at: instant,
    details: clientReportDetails,
  })
  .strict();

const clientReportRequest = z
  .object({
    client: clientReportClient,
    events: z.array(clientReportEvent).min(1).max(CLIENT_REPORT_LIMITS.eventsPerReport),
  })
  .strict();

export interface FrozenOperationSchemas {
  readonly request: z.ZodType;
  readonly response: z.ZodType;
}

/** Explicit top-level shapes; domain-specific invariants remain in the pure domain package/handlers. */
export const OPERATION_SCHEMAS = Object.freeze({
  'account.bootstrap': { request: empty, response: z.object({ status: accountStatus, role, enrollmentsCreated: z.number().int().nonnegative(), invitesConsumed: z.number().int().nonnegative(), conflicts: z.array(jsonObject) }).strict() },
  'me.state': { request: empty, response: z.object({ status: accountStatus, role }).strict() },
  // *(Owner, 2026-10-12)* Browser-side errors and diagnostics, written to Cloud Logging only.
  'client.report': { request: clientReportRequest, response: z.object({ accepted: wholeCount }).strict() },
  'me.get': { request: empty, response: meResponseSchema },
  'courses.listMine': { request: empty, response: z.object({ courses: z.array(course) }).strict() },
  'courses.get': { request: z.object({ courseId: id }).strict(), response: course },
  // *(T060 D1)* A material is `courses/{courseId}/sections/{sectionId}/materials/{materialId}`, so the
  // student download carries the same complete path the T059 administrator operations do. The response
  // is the expiring URL and its instant: the object path never reaches a browser
  // (`ARCHITECTURE.md` § 10, `BOUNDARIES.md` § 5.4).
  'materials.fileAccess': { request: z.object({ courseId: id, sectionId: id, materialId: id }).strict(), response: z.object({ url: z.url(), expiresAt: instant }).strict() },
  // *(2026-09-26)* One reading material's body, kept inline or in Storage — the same document either way.
  'materials.body': { request: materialPath, response: z.object({ richText: richTextContentSchema }).strict() },
  'questions.submit': { request: z.object({ courseId: id, text: z.string().trim().min(1).max(3_000) }).strict(), response: z.object({ questionId: id }).strict() },
  'questions.deleteMine': { request: z.object({ questionId: id }).strict(), response: z.object({ deletedAt: instant }).strict() },
  'questions.list': { request: questionListRequest, response: questionPage },
  'questions.search': { request: z.object({ q: z.string().trim().min(2).max(500), courseId: id.optional(), scope: visibilityScope, cursor: cursor.optional() }).strict(), response: searchResponse },
  'questions.get': { request: z.object({ questionId: id }).strict(), response: questionListItemSchema },
  'favorites.toggle': { request: z.object({ questionId: id }).strict(), response: z.object({ favorited: z.boolean() }).strict() },
  // *(OD-2026-09-24, OD-SQ4)* `courseId` narrows the list to one course's favourites; without it the
  // list spans every course, exactly as before.
  'favorites.list': { request: z.object({ cursor: cursor.optional(), courseId: id.optional() }).strict(), response: questionPage.omit({ me: true }) },
  /*
   * *(002-course-content-reader)* The batched student read of protected images (FR-012).
   *
   * One coherent entitlement snapshot for every asset of one visible document. The response carries
   * only expiring URLs, so nothing a student receives survives its five-minute window.
   */
  'contentAssets.fileAccess': { request: assetAccessRequest, response: assetAccessResponse },
  /*
   * *(002-course-content-reader)* The seven private reader operations (FR-025 … FR-032).
   *
   * Every one of them carries the full material path, because authority is re-established on the
   * server for each call rather than trusted from the session that opened the material.
   */
  'reader.open': {
    request: materialPath,
    response: z
      .object({
        sessionId: id,
        epoch: z.number().int().nonnegative(),
        contentVersionId: id.nullable(),
        lastPosition: jsonObject.nullable(),
        revision: wholeCount,
        highlights: z.array(jsonObject).max(MAX_READER_RECORDS),
        bookmarks: z.array(jsonObject).max(MAX_READER_RECORDS),
      })
      .strict(),
  },
  /*
   * The convergence rule in one shape: an older epoch or a non-increasing sequence answers
   * `applied:false` and the current position is returned unchanged, so the losing device learns where
   * the winner actually is instead of retrying into the same refusal (R4, FR-031).
   */
  'reader.progress.save': {
    request: materialPath.extend({ session: readerSession, position: readerPosition }).strict(),
    response: z
      .object({ applied: z.boolean(), revision: wholeCount, lastPosition: jsonObject.nullable() })
      .strict(),
  },
  'reader.highlights.create': {
    request: materialPath
      .extend({
        highlightId: readerRecordId,
        contentVersionId: id,
        ...structuralRange.shape,
        color: highlightColor,
        // *(OD-2026-09-23)* An optional private note on the highlight.
        note: highlightNote.optional(),
      })
      .strict(),
    response: itemResponse,
  },
  /*
   * Only the color or *(OD-2026-09-23)* the private note changes; a highlight never moves to
   * different words without being recreated. At least one of the two is named.
   */
  'reader.highlights.update': {
    request: materialPath
      .extend({ highlightId: readerRecordId, expectedRevision, color: highlightColor.optional(), note: highlightNote.optional() })
      .strict(),
    response: itemResponse,
  },
  'reader.highlights.delete': {
    request: materialPath.extend({ highlightId: readerRecordId, expectedRevision }).strict(),
    response: okResponse,
  },
  /*
   * *(Re-audit N02, OD-SR2)* A new highlight that takes the place of the ones its passage overlaps,
   * in one step: the create's own fields, plus each overlapped highlight with the revision it is
   * expected to still be at. Either every named highlight is removed and the new one stored, or
   * nothing changes — and the cap applies to the count *after* the replacement, so a full material
   * can still have an overlap replaced. The note is the caller's (OD-SR2 keeps the first overlapped
   * note; the client decides which that is and sends it here).
   */
  'reader.highlights.replace': {
    request: materialPath
      .extend({
        highlightId: readerRecordId,
        contentVersionId: id,
        ...structuralRange.shape,
        color: highlightColor,
        note: highlightNote.optional(),
        replaces: z
          .array(z.object({ highlightId: readerRecordId, expectedRevision }).strict())
          .min(1)
          .max(MAX_HIGHLIGHT_REPLACEMENTS),
      })
      .strict()
      .refine(
        (value) => new Set([value.highlightId, ...value.replaces.map((entry) => entry.highlightId)]).size ===
          value.replaces.length + 1,
        // Data-free: a replaced highlight is named once, and never as the new highlight itself.
        { message: 'each replaced highlight is named once and differs from the new one', path: ['replaces'] },
      ),
    response: itemResponse,
  },
  /* *(FR-025)* Several per material, each at a stable content position and with no free-form note. */
  'reader.bookmarks.create': {
    request: materialPath
      .extend({ bookmarkId: readerRecordId, position: readerPosition })
      .strict(),
    response: itemResponse,
  },
  'reader.bookmarks.delete': {
    request: materialPath.extend({ bookmarkId: readerRecordId, expectedRevision }).strict(),
    response: okResponse,
  },
  /*
   * *(PRD-07 § 3.1, FREEZE-1)* The explicit, reversible completion toggle.
   *
   * **Desired state, not a verb.** `completed` says what the student wants to be true afterwards, so a
   * repeat, a retry and an out-of-order request are all answerable without a revision, a session or an
   * idempotency id: setting the stored state performs no write and answers `applied:false` with that
   * stored state. A request can never flip a value it did not name, which is what makes two devices
   * converge on the later server write instead of toggling each other.
   *
   * `completedAt` is the **server** clock of the last `completed:true` transition and is `null`
   * whenever `completed` is `false`. It is not a caller-supplied timestamp: a browser clock deciding
   * when a material was finished would be a browser deciding the order of two devices' writes.
   *
   * The path is the same `materialPath` every reader call carries, because authority is re-established
   * on the server per call — approved account, own active opened enrolment, live course and group,
   * published section of this student's scope, and a material currently released to them.
   */
  'materials.completion.set': {
    request: materialPath.extend({ completed: z.boolean() }).strict(),
    response: z
      .object({ applied: z.boolean(), completed: z.boolean(), completedAt: instant.nullable() })
      .strict(),
  },

  'admin.requests.list': { request: adminPageRequest.extend({ status: z.enum(['pending', 'approved', 'rejected']).optional() }).strict(), response: pageOf(jsonObject) },
  /*
   * *(AR-11 correction)* One access request, by id.
   *
   * The redesign puts the selected request in the address, so a reload, a bookmark and a shared link
   * all arrive with no list page loaded. Resolving that address out of whatever page the queue
   * happens to hold made a real record read "not on this page", which is a defect of the surface and
   * not a fact about the record. This is the read that answers it, and it adds no capability: an
   * administrator may already list every request.
   */
  'admin.requests.get': { request: z.object({ requestId: id }).strict(), response: itemResponse },
  'admin.requests.decide': { request: batchRequest(z.object({ decision: z.enum(['approve', 'reject']) }).strict()), response: batchResponseSchema },
  // *(CE-1, CE-10)* A row names its target group, or — for a continuing-education course only — the
  // course itself, whose one technical group the server resolves. Exactly one of the two.
  'admin.preInvites.create': { request: z.object({ rows: z.array(exactlyOneTarget(z.object({ email: z.email(), firstName: z.string().max(100).optional(), lastName: z.string().max(100).optional(), phone: z.string().max(50).optional(), groupId: id.optional(), courseId: id.optional() }).strict())).min(1).max(500), batchKey: batchKey.optional() }).strict(), response: batchResponseSchema },
  'admin.preInvites.list': { request: adminPageRequest.extend({ status: z.enum(['open', 'consumed', 'conflict']).optional(), groupId: id.optional(), courseId: id.optional(), email: z.string().optional() }).strict(), response: pageOf(jsonObject) },
  'admin.preInvites.update': { request: batchRequest(z.object({ firstName: optionalName, lastName: optionalName, phone: z.string().max(50).nullable().optional(), groupId: id.optional() }).strict()), response: batchResponseSchema },
  'admin.preInvites.delete': { request: batchRequest(z.object({}).strict()), response: batchResponseSchema },
  'admin.students.list': { request: listFilters.extend({ status: accountStatus.optional(), courseId: id.optional(), groupId: id.optional() }).strict(), response: pageOf(jsonObject) },
  'admin.students.get': { request: z.object({ accountId: id }).strict(), response: itemResponse },
  'admin.students.update': { request: profilePatch.extend({ accountId: id }).strict(), response: itemResponse },
  'admin.students.setStatus': { request: batchRequest(z.object({ status: z.enum(['deactivate', 'reactivate']) }).strict()), response: batchResponseSchema },
  'admin.students.recordDeletionRequest': { request: z.object({ accountId: id }).strict(), response: z.object({ requestId: id }).strict() },
  /*
   * *(OD-2026-09-24, OD-S1/S3/S4/S13)* The Admin Students register.
   *
   * One read over two collections, because the design lists a person before they have an account:
   * approved and suspended accounts, and open or conflicting pre-invitations grouped by e-mail.
   * Pending and rejected accounts stay on Admin Home (OD-S1) and consumed invitations appear as the
   * account they became. `status` is the chip: `ending` is an approved account with an active
   * enrolment that ends within fourteen days or has already ended (OD-S3). `counts` are the chip
   * totals over the whole register, independent of `q`, `courseId` and `status`, exactly as the
   * design counts them; `total` is the size of the filtered set the cursor walks.
   */
  'admin.students.directory': {
    request: adminPageRequest
      .extend({
        q: z.string().trim().max(500).optional(),
        status: z.enum(STUDENT_DIRECTORY_STATUSES).default('all'),
        courseId: id.optional(),
        sort: z.enum(STUDENT_DIRECTORY_SORTS).default('newest'),
      })
      .strict(),
    response: z
      .object({
        items: z.array(jsonObject).max(50),
        nextCursor: cursor.optional(),
        total: wholeCount,
        counts: z
          .object({ all: wholeCount, active: wholeCount, invited: wholeCount, suspended: wholeCount, ending: wholeCount })
          .strict(),
      })
      .strict(),
  },
  /*
   * *(OD-2026-09-24, OD-S6)* One student's per-course progress, as the student's own course card
   * computes it: completed materials over the course total, where a locked (scheduled) material counts
   * in the total and a hidden one does not. Counts only — never which material, never a completion
   * instant and never a reading position (FR-028 as amended).
   */
  'admin.students.progress': {
    request: z.object({ accountId: id }).strict(),
    response: z
      .object({
        items: z.array(z.object({
          enrollmentId: id,
          courseId: id,
          groupId: id,
          completedCount: wholeCount,
          visibleCount: wholeCount,
        }).strict()),
      })
      .strict(),
  },

  // *(T091)* The bounded administrator read side, named by accepted `tasks.md` T091 rather than by
  // `contracts/operations.md`, exactly as `admin.materials.beginUpload`/`finishUpload` are — see the
  // derivation note in `./ops.ts`. Both list items reuse the T053 `courseItemOf`/`groupItemOf`
  // projections, so a list row and a create/update `item` are one shape.
  /*
   * *(AR-12 correction)* The names behind a bounded set of course and group identifiers.
   *
   * Stored records — an enrolment, a question, an invitation — name their course and group by id.
   * The interface must never show those, so it needs the names; reading them out of the *first page*
   * of a directory left the twenty-first course nameless. This asks for exactly the identifiers a
   * rendered page references, at most fifty of each, and answers with their names. It discloses
   * nothing new: both directories are already listable by the same administrator.
   */
  'admin.curriculum.names': { request: z.object({ courseIds: z.array(id).max(50).default([]), groupIds: z.array(id).max(50).default([]) }).strict(), response: z.object({ courses: z.array(jsonObject), groups: z.array(jsonObject) }).strict() },
  /*
   * *(ledger 59)* The bounded whole-scope totals a course or group overview states.
   *
   * The owner asks both overviews for facts about the **whole** scope: "per-student and total question
   * counts … total/held/remaining lectures" for a group, and "the analogous course facts plus group
   * count" for a course. Every one of those is a number over a set the overview does not — and must
   * not — load: summing the first page of a roster and calling the result the group's total is a
   * figure that changes with the page size, which is worse than no figure at all.
   *
   * So the server projects them. `questionTotal` and `groupCount` are Firestore aggregates, which
   * cost one request each however large the set is; the lecture totals are summed from the course's
   * own groups under the same bounded scan every curriculum read uses. `groupId` narrows all three to
   * one group of that course; without it they describe the course.
   *
   * It discloses nothing new: the same administrator already lists both directories and every
   * question of this scope.
   */
  'admin.curriculum.overview': { request: z.object({ courseId: id, groupId: id.optional() }).strict(), response: z.object({ questionTotal: wholeCount, groupCount: wholeCount, lectures: z.object({ total: wholeCount, held: wholeCount, remaining: wholeCount }).strict() }).strict() },
  'admin.courses.list': { request: curriculumListFilters, response: pageOf(jsonObject) },
  // *(T059)* `previewGroupId` is the one selected group the administrator opening preview is computed
  // for (FR-MAT-08). It is optional because the structure is also browsed without one, and it is a
  // single id rather than a list because a course-wide preview would multiply every material row by
  // every group of the course.
  'admin.courses.get': { request: z.object({ courseId: id, previewGroupId: id.optional() }).strict(), response: z.object({ course: jsonObject, sections: z.array(jsonObject) }).strict() },
  // *(OD-2026-09-23)* A course may be created as a `draft`; without `status` it is `active` as before.
  'admin.courses.create': {
    request: courseInput.extend({ status: z.enum(['draft', 'active']).optional() }).strict(),
    response: itemResponse,
  },
  'admin.courses.update': { request: courseInput.partial().extend({ courseId: id }).strict(), response: itemResponse },
  'admin.courses.archive': { request: z.object({ courseId: id }).strict(), response: z.object({ courseId: id, status: z.literal('archived') }).strict() },
  // *(OD-2026-09-23)* The one way out of `draft`: the course becomes `active` and open to enrolment.
  'admin.courses.activate': { request: z.object({ courseId: id }).strict(), response: z.object({ courseId: id, status: z.literal('active') }).strict() },
  /*
   * *(OD-7)* The four course lifecycle operations `admin-redesign-addendum.md` § 8.6 names.
   *
   * They are **additions**: `admin.courses.archive` keeps its exact request and response, and every
   * caller written before § 8 is untouched. `unarchive` is the explicit way back the older text
   * deliberately did not offer, `deletePreview` writes nothing at all, `delete` moves the aggregate
   * into the thirty-day Trash window, and `restore` clears the markers while the purge has not begun.
   *
   * All four take only the course id: a deletion whose safety depended on a caller-supplied count
   * would be a deletion the caller could talk the server into, so every precondition is re-read
   * inside the server's own write transaction (§ 8.6).
   */
  'admin.courses.unarchive': { request: lifecycleCourseRequest, response: z.object({ courseId: id, status: z.literal('active') }).strict() },
  'admin.courses.deletePreview': { request: lifecycleCourseRequest, response: lifecycleImpactSchema },
  'admin.courses.delete': { request: lifecycleCourseRequest, response: lifecycleStateSchema },
  'admin.courses.restore': { request: lifecycleCourseRequest, response: lifecycleStateSchema },
  /*
   * *(OD-2026-09-24, OD-C12)* The administrator's permanent delete of a course already in the Trash:
   * the retention run's aggregate purge without the thirty-day wait. `CONFLICT` means a large course
   * is still being removed (or the retention run holds it) — retry; a repeat after success answers
   * `purged: true` again.
   */
  'admin.courses.purge': { request: lifecycleCourseRequest, response: z.object({ purged: z.literal(true) }).strict() },
  /*
   * *(AR-09)* Explicit, previewed template propagation.
   *
   * The request is the project's own frozen batch envelope — `{ids, params, batchKey?}` — rather than
   * a new shape, so the parent batch, the child audit events, the `batchKey` replay rule and the
   * applied/skipped/failed reporting are the ones every other batch already has, and the client's
   * `readBatch`, batch preview and result notice work unchanged. `ids` are the groups to update and
   * `params.courseId` is the course whose template is being propagated; a group that does not belong
   * to it is skipped rather than silently retargeted.
   */
  'admin.courses.applyTemplatePreview': { request: templatePropagationRequest(z.object({ courseId: id }).strict()), response: templatePreviewResponseSchema },
  'admin.courses.applyTemplate': { request: templatePropagationRequest(z.object({ courseId: id, templateVersion }).strict()), response: batchResponseSchema },
  // *(ledger 56)* `courseIds` is the register's searchable multiple course selection; `courseId` is
  // kept so every existing caller and link is unchanged. Both narrow the same field.
  'admin.groups.list': { request: curriculumListFilters.extend({ courseId: id.optional(), courseIds: z.array(id).min(1).max(MAX_FILTER_VALUES).optional() }).strict(), response: pageOf(jsonObject) },
  'admin.groups.create': { request: groupFields, response: itemResponse },
  /*
   * *(AR-09 correction)* An update carries **no** template.
   *
   * A group's template changes on exactly two paths: it is inherited at creation, and it is
   * propagated by the previewed pair above, which refuses a group that has started, is archived or
   * belongs to another course. Leaving `template` on this generic patch was a third path with none
   * of those rules — a started group could be retagged silently — so the field is omitted here and
   * the server ignores nothing it was never sent.
   */
  'admin.groups.update': { request: groupFields.partial().omit({ template: true }).extend({ groupId: id }).strict(), response: itemResponse },
  'admin.groups.archive': { request: z.object({ groupId: id }).strict(), response: z.object({ groupId: id, status: z.literal('archived') }).strict() },
  // *(OD-7)* The group equivalents of the four course lifecycle operations, over the group id.
  'admin.groups.unarchive': { request: lifecycleGroupRequest, response: z.object({ groupId: id, status: z.literal('active') }).strict() },
  'admin.groups.deletePreview': { request: lifecycleGroupRequest, response: lifecycleImpactSchema },
  'admin.groups.delete': { request: lifecycleGroupRequest, response: lifecycleStateSchema },
  'admin.groups.restore': { request: lifecycleGroupRequest, response: lifecycleStateSchema },
  // *(T059)* Sections and materials are Firestore subcollections
  // (`courses/{courseId}/sections/{sectionId}/materials/{materialId}`), so a bare document id names
  // nothing the server can address: `id` forbids a path separator, and a collection-group lookup
  // cannot compare a document id without the full path either. Every operation below therefore
  // carries its complete parent path — on the batch operations inside `params`, which is the only
  // place their frozen envelope has for it. None of them is called by an accepted client yet, so
  // requiring the identity they always needed migrates nothing.
  'admin.sections.create': { request: sectionFields, response: itemResponse },
  'admin.sections.update': { request: sectionFields.partial().extend({ courseId: id, sectionId: id }).strict(), response: itemResponse },
  // The ids array is the desired order; one shared numeric order would be ambiguous for a batch.
  'admin.sections.reorder': { request: batchRequest(z.object({ courseId: id }).strict()), response: batchResponseSchema },
  'admin.sections.setPublished': { request: batchRequest(z.object({ courseId: id, published: z.boolean() }).strict()), response: batchResponseSchema },
  /*
   * *(OD-2026-10-12, FR-MAT-03)* The thirty-day section Trash. `delete` sets `deletedAt`/`deletedBy` and
   * cascades nothing; every read treats the section and everything in it as absent until `restore`.
   */
  'admin.sections.deletePreview': {
    request: z.object({ courseId: id, sectionId: id }).strict(),
    response: z.object({
      sectionId: id,
      title: z.string(),
      scope: z.enum(['course', 'group']),
      groupId: id.nullable(),
      materialCount: wholeCount,
      trashedMaterialCount: wholeCount,
      fileCount: wholeCount,
      movingCount: wholeCount,
    }).strict(),
  },
  'admin.sections.delete': {
    request: z.object({ courseId: id, sectionId: id }).strict(),
    response: z.object({ sectionId: id, deletedAt: instant, deletedBy: id.nullable(), purgeAfter: instant }).strict(),
  },
  'admin.sections.restore': {
    request: z.object({ courseId: id, sectionId: id }).strict(),
    response: z.object({ sectionId: id, restored: z.literal(true) }).strict(),
  },
  'admin.sections.listDeleted': {
    request: z.object({ courseId: id }).strict(),
    response: z.object({ items: z.array(jsonObject).max(MAX_DELETED_SECTIONS_LISTED), total: wholeCount }).strict(),
  },
  'admin.materials.create': { request: materialFields, response: itemResponse },
  'admin.materials.update': { request: materialFields.partial().extend({ courseId: id, sectionId: id, materialId: id }).strict(), response: itemResponse },
  'admin.materials.reorder': { request: batchRequest(z.object({ courseId: id, sectionId: id }).strict()), response: batchResponseSchema },
  'admin.materials.setPublished': { request: batchRequest(z.object({ courseId: id, sectionId: id, published: z.boolean() }).strict()), response: batchResponseSchema },
  /*
   * *(OD-2026-09-24, OD-G6)* The thirty-day material Trash.
   *
   * `delete` and `restore` are the project's frozen batch envelope over one section, exactly as
   * `reorder` and `setPublished` are. A deleted material leaves every student and administrator read
   * at once; `restore` brings it back unchanged while the retention run has not begun removing it.
   * `listDeleted` is the bounded read of one course's Trash (optionally one group's), newest deletion
   * first, at most 200 rows with the exact `total` beside them.
   */
  'admin.materials.delete': { request: batchRequest(z.object({ courseId: id, sectionId: id }).strict()), response: batchResponseSchema },
  'admin.materials.restore': { request: batchRequest(z.object({ courseId: id, sectionId: id }).strict()), response: batchResponseSchema },
  'admin.materials.listDeleted': {
    request: z.object({ courseId: id, groupId: id.optional() }).strict(),
    response: z.object({ items: z.array(jsonObject).max(MAX_DELETED_MATERIALS_LISTED), total: wholeCount }).strict(),
  },
  /*
   * *(OD-2026-10-12, FR-MAT-07)* A material into another section of its course: copied with the same ids
   * and flipped in one transaction, keeping its versions, explanations, files, images and every
   * student's private rows. Idempotent and resumable by `moveKey`; `pending` asks for a retry with it.
   */
  'admin.materials.move': {
    request: z.object({
      courseId: id,
      sectionId: id,
      materialId: id,
      targetSectionId: id,
      order: z.number().int().nonnegative().optional(),
      groupIds: z.array(id).max(50).nullable().optional(),
      moveKey: id,
    }).strict(),
    response: z.object({ state: z.enum(['done', 'pending']), item: jsonObject.optional(), sectionId: id }).strict(),
  },
  // *(T060 D1/D2)* The two-step upload, corrected for the same nested identity — and for one rule the
  // section/material operations do not need: **no storage coordinate crosses the wire in either
  // direction**. A caller-supplied `storagePath` would contradict `ARCHITECTURE.md` § 10 and would be
  // an object-path injection surface, letting an administrator attach any object in the bucket to a
  // material. `uploadId` replaces it: the server mints it, it names exactly one server-derived
  // candidate object, and it carries the file revision that candidate was issued against (D3).
  //
  // `size` is declared at `beginUpload` only so an over-cap upload is refused *before* five minutes of
  // bytes are sent (FR-MAT-10 requires a clear size message); the authority on what was actually
  // stored is Storage metadata, re-checked at `finishUpload`. `fileName` belongs to `finishUpload`,
  // which is where the server attaches it, so nothing has to be remembered between the two calls.
  'admin.materials.body': { request: materialPath, response: z.object({ richText: richTextContentSchema }).strict() },
  'admin.materials.beginUpload': { request: z.object({ courseId: id, sectionId: id, materialId: id, contentType: z.literal('application/pdf'), size: z.number().int().positive() }).strict(), response: z.object({ uploadUrl: z.url(), uploadId: id, expiresAt: instant }).strict() },
  'admin.materials.finishUpload': { request: z.object({ courseId: id, sectionId: id, materialId: id, uploadId: id, fileName: shortText }).strict(), response: itemResponse },
  'admin.materials.deleteFile': { request: z.object({ courseId: id, sectionId: id, materialId: id }).strict(), response: okResponse },
  // *(T062 D1)* The administrator preview download, whose request and response are deliberately the
  // **same shapes** as the student `materials.fileAccess` above.
  //
  // R2-02 requires one production PDF reader shared by student reading and the administrator's
  // selected-group preview, and a reader can only be one component if both of its sources answer with
  // one shape. What differs is entirely server-side: this operation proves a *current administrator*
  // instead of an authorized enrolment, so a draft, unpublished, hidden or not-yet-scheduled file can
  // be previewed by its author. The selected group's `hidden | scheduled | visible | missing-schedule`
  // state stays where it already is — `admin.courses.get({previewGroupId})` — rather than being
  // duplicated here, which is why this request carries no `previewGroupId`.
  //
  // Like every protected-file operation it addresses the nested material by its complete path and
  // returns no storage coordinate: only the expiring URL and its instant.
  'admin.materials.fileAccess': { request: z.object({ courseId: id, sectionId: id, materialId: id }).strict(), response: z.object({ url: z.url(), expiresAt: instant }).strict() },
  /*
   * *(002-course-content-reader)* The protected content-asset handshake, generalizing T060's accepted
   * PDF flow to course covers and inline images.
   *
   * `beginUpload` signs one create-only candidate URL for at most five minutes and returns **no**
   * storage coordinate — the object is named by `uploadId` alone, exactly as it is for a PDF.
   * `finishUpload` is where the claim becomes a fact: the server reads the stored object's own
   * metadata and magic bytes and answers with the verified type, size and pixel dimensions, so a
   * caller can never talk an asset into existing by describing it.
   */
  'admin.contentAssets.beginUpload': {
    request: z
      .object({
        purpose: contentAssetPurpose,
        contentType: imageContentType,
        size: z.number().int().positive().max(MAX_CONTENT_IMAGE_BYTES),
      })
      .strict(),
    response: z.object({ uploadId: id, uploadUrl: z.url(), expiresAt: instant }).strict(),
  },
  'admin.contentAssets.finishUpload': {
    request: z.object({ uploadId: id, fileName: shortText }).strict(),
    response: z
      .object({
        assetId: id,
        contentType: imageContentType,
        size: z.number().int().positive().max(MAX_CONTENT_IMAGE_BYTES),
        widthPx: z.number().int().positive(),
        heightPx: z.number().int().positive(),
      })
      .strict(),
  },
  /* The explicit way back for a candidate its creator abandoned; an attached asset is not reachable. */
  'admin.contentAssets.discard': {
    request: z.object({ assetId: id }).strict(),
    response: okResponse,
  },
  /* The administrator equivalent of the student batch read — it may also see a draft's assets. */
  'admin.contentAssets.fileAccess': { request: assetAccessRequest, response: assetAccessResponse },
  /*
   * *(002-course-content-reader)* The five material-explanation operations (FR-018 … FR-021).
   *
   * `create` and `reanchor` are the only two that name a passage, and both quote the
   * `contentVersionId` they measured it in: a passage named against a stale version is a
   * `FAILED_PRECONDITION`, never a best-effort placement. `update` deliberately carries no endpoints
   * at all, so editing an explanation's text can never move it.
   */
  'admin.materialExplanations.list': {
    request: materialPath,
    response: z
      .object({ contentVersionId: id.nullable(), explanations: z.array(jsonObject).max(200) })
      .strict(),
  },
  'admin.materialExplanations.create': {
    request: materialPath
      .extend({ contentVersionId: id, ...structuralRange.shape, ...explanationContent.shape })
      .strict(),
    response: itemResponse,
  },
  'admin.materialExplanations.update': {
    request: materialPath
      .extend({ explanationId: id, expectedRevision, ...explanationContent.shape })
      .strict(),
    response: itemResponse,
  },
  'admin.materialExplanations.delete': {
    request: materialPath.extend({ explanationId: id, expectedRevision }).strict(),
    response: okResponse,
  },
  'admin.materialExplanations.reanchor': {
    request: materialPath
      .extend({
        explanationId: id,
        expectedRevision,
        contentVersionId: id,
        ...structuralRange.shape,
      })
      .strict(),
    response: itemResponse,
  },

  // *(CE-1, CE-10)* Same target rule as a pre-invitation: `groupId`, or a continuing-education `courseId`.
  'admin.enrollments.create': { request: exactlyOneTarget(z.object({ accountIds: z.array(id).min(1).max(500), groupId: id.optional(), courseId: id.optional(), openedAt: instant.optional(), batchKey: batchKey.optional() }).strict()), response: batchResponseSchema },
  'admin.enrollments.transfer': { request: z.object({ enrollmentId: id, toGroupId: id, newEnd: instant.optional(), note: z.string().max(500).optional() }).strict(), response: itemResponse },
  'admin.enrollments.close': { request: batchRequest(z.object({}).strict()), response: batchResponseSchema },
  'admin.entitlements.extendPreview': { request: entitlementRequest, response: entitlementResponse },
  'admin.entitlements.extend': { request: entitlementRequest, response: entitlementResponse },

  'admin.questions.list': { request: adminQuestionFilters, response: pageOf(jsonObject) },
  /*
   * *(T096)* The detail gains the question's selection edits.
   *
   * An **addition** to the response, and the only kind this contract admits: a key every reader may
   * ignore, always present so nobody has to branch on its absence, and carrying named safe fields
   * rather than stored documents (`selectionEditView`). It is administrator-only by construction —
   * this operation is on `adminApi` — and no student response gains anything.
   */
  'admin.questions.get': { request: z.object({ questionId: id }).strict(), response: z.object({ question: jsonObject, textVersions: z.array(jsonObject), answerVersions: z.array(jsonObject), auditEvents: z.array(jsonObject), selectionEdits: z.array(jsonObject), lastAttempt: jsonObject.nullable() }).strict() },
  'admin.questions.editText': { request: z.object({ questionId: id, text: z.string().trim().min(1).max(3_000) }).strict(), response: itemResponse },
  'admin.questions.setVisibilityScope': { request: z.object({ questionId: id, visibilityScope: questionVisibilityScope }).strict(), response: itemResponse },
  'admin.answers.saveDraft': { request: z.object({ questionId: id, doc: richTextDocSchema, expectedVersion: z.number().int().nonnegative() }).strict(), response: itemResponse },
  'admin.answers.regenerate': { request: z.object({ questionId: id }).strict(), response: z.object({ enqueued: z.boolean() }).strict() },
  'admin.answers.overrideStale': { request: z.object({ questionId: id }).strict(), response: itemResponse },
  'admin.answers.confirm': { request: batchRequest(z.object({ preview: z.boolean().optional() }).strict()), response: batchResponseSchema },
  'admin.answers.editPublished': { request: z.object({ questionId: id, doc: richTextDocSchema, expectedVersion: z.number().int().nonnegative() }).strict(), response: itemResponse },
  'admin.answers.unpublish': { request: batchRequest(z.object({}).strict()), response: batchResponseSchema },
  /*
   * *(T094)* The restore: one question, the stored version to restore **by id**, and the same
   * optimistic guard `saveDraft` and `editPublished` carry.
   *
   * No document is accepted. The content comes from the stored version the id names, so a caller can
   * never "restore" text no version ever held, and the server re-validates that stored document
   * against the allowlist before it writes anything.
   */
  'admin.answers.restoreVersion': { request: z.object({ questionId: id, versionId: id, expectedVersion: z.number().int().nonnegative() }).strict(), response: itemResponse },
  /*
   * *(T095)* The selection-scoped AI edit.
   *
   * A create names **coordinates into the server's own stored document** — the answer version, the
   * structural path of the block and two character offsets inside that block's text — and never the
   * selected text, a hash or a replacement. The server derives all three from the stored version, so a
   * caller can only point at data the server already holds; anything it claimed about the content
   * would be a claim the server would have to trust.
   *
   * `retry` and `replace` name the job. `replace` carries the same optimistic `expectedVersion` guard
   * every other administrator answer write carries, because applying a replacement appends a version.
   */
  /*
   * *(002-course-content-reader, FR-001)* The multi-block amendment.
   *
   * `blockPath` plus two character offsets could only ever name a range **inside one leaf block** —
   * the refusal the previous increment documented as a deliberate limit. Two structural endpoints name
   * a range across adjacent paragraphs, headings, list items and blockquote content instead, and the
   * server derives the one contiguous range between them from the stored document. Everything else
   * about the job is unchanged: the instruction, the source version, the retry, the substatus, the
   * audit, the exact preservation of content outside the range and the restorable answer version.
   */
  'admin.selectionEdits.create': { request: z.object({ questionId: id, versionId: id, ...structuralRange.shape, instruction: selectionInstruction }).strict(), response: itemResponse },
  'admin.selectionEdits.retry': { request: z.object({ questionId: id, jobId: id }).strict(), response: itemResponse },
  // *(AS-009)* `draftOnly` refuses the replacement, inside the transaction, once the answer is published.
  'admin.selectionEdits.replace': { request: z.object({ questionId: id, jobId: id, expectedVersion: z.number().int().nonnegative(), draftOnly: z.boolean().optional() }).strict(), response: itemResponse },
  'admin.ai.prepareNow': { request: batchRequest(z.object({}).strict()), response: batchResponseSchema },
  'admin.ai.prepareAll': { request: empty, response: z.object({ enqueued: z.number().int().nonnegative(), skipped: z.number().int().nonnegative() }).strict() },
  'admin.questions.delete': { request: batchRequest(z.object({}).strict()), response: batchResponseSchema },
  // *(OD-2026-09-23)* T14 restore (batch, the delete's mirror) and the administrator's permanent
  // delete of one question that is already in the deleted list.
  'admin.questions.restore': { request: batchRequest(z.object({}).strict()), response: batchResponseSchema },
  'admin.questions.purge': {
    request: z.object({ questionId: id }).strict(),
    response: z.object({ purged: z.literal(true) }).strict(),
  },

  'admin.audit.list': { request: auditFilters, response: pageOf(jsonObject) },
  'admin.batches.get': { request: z.object({ batchId: id }).strict(), response: z.object({ batch: jsonObject, children: z.array(jsonObject) }).strict() },
  // *(T070)* `needsAttention` is the one amendment this task makes to an already-frozen shape, merged
  // by the integrator and recorded in `docs/HANDOFF.md` as `BOUNDARIES.md` § 3 requires. FR-OPS-04 and
  // accepted `tasks.md` T070 both promise the administrator a *visible* attention list as well as its
  // total, and `needsAttentionCount` alone could not carry one. It is required rather than optional so
  // a server that computed nothing cannot answer indistinguishably from one that found nothing, and it
  // is capped at the 50 rows every other administrator page uses — the uncapped total stays its own
  // field, so a client can tell "all of them" from "the newest 50 of N".
  'admin.system.status': { request: empty, response: z.object({ lastAiRun: jsonObject.nullable(), needsAttentionCount: z.number().int().nonnegative(), needsAttention: z.array(jsonObject).max(50), lastBackup: jsonObject.nullable(), lastPurge: jsonObject.nullable() }).strict() },

  'system.ai.hourlyRun': { request: empty, response: z.object({ runId: id, picked: z.number().int().nonnegative(), enqueued: z.number().int().nonnegative(), skipped: z.number().int().nonnegative() }).strict() },
  // *(T039)* `skipped_ineligible` and `lease_lost` are the two outcomes the worker could not report
  // truthfully before: a question that stopped being eligible between selection and delivery (author
  // deactivated, draft saved, status moved, attempt cap reached) is not a failure and must not burn an
  // attempt, and a second transaction that finds the lease taken is not a duplicate delivery — it lost a
  // race it must report as itself rather than borrow another outcome's name.
  'system.ai.processQuestion': { request: z.object({ questionId: id, trigger: z.enum(['hourly', 'manual_one', 'manual_all']), requestedBy: id.optional() }).strict(), response: z.object({ outcome: z.enum(['success', 'failure', 'skipped_deleted', 'skipped_ineligible', 'discarded_deleted', 'lease_active', 'lease_lost']) }).strict() },
  /*
   * *(T095)* One selection-edit job. The outcomes are the worker's own, and they are deliberately not
   * the question worker's: a selection job is never "skipped ineligible" and never publishes anything,
   * and the two that look alike — a lost lease and a discarded result — mean the same thing they mean
   * there, which is why they keep those names.
   */
  'system.ai.processSelectionEdit': { request: z.object({ questionId: id, jobId: id }).strict(), response: z.object({ outcome: z.enum(['ready', 'failed', 'skipped_not_queued', 'skipped_gone', 'lease_active', 'lease_lost']) }).strict() },
  'system.retention.purgeDaily': { request: empty, response: z.object({ questionsPurged: z.number().int().nonnegative(), aiCallsPurged: z.number().int().nonnegative(), accountsAnonymized: z.number().int().nonnegative() }).strict() },
} as const satisfies Readonly<Record<string, FrozenOperationSchemas>>);
